<template>
    <div class="fh" role="main" aria-labelledby="feedback-hub-title">
        <!-- Kopfbereich: gleiche dunkle Karte wie auf der Startseite -->
        <section class="fh-hero">
            <div class="fh-hero-text">
                <span class="fh-eyebrow"><v-icon size="16" aria-hidden="true">mdi-lightbulb-on-outline</v-icon>Feedback Hub</span>
                <h1 id="feedback-hub-title" class="fh-hero-title">Help shape Quaro</h1>
                <p class="fh-hero-sub">Share ideas, vote for what matters most to you and follow what we are working on. All feedback is shown anonymously.</p>
                <button type="button" class="fh-cta" @click="openWidget"><v-icon size="18" aria-hidden="true">mdi-message-plus-outline</v-icon>Give feedback</button>
            </div>
            <ul class="fh-stats" aria-label="Overview">
                <li v-for="stat in heroStats" :key="stat.tab">
                    <button type="button" class="fh-stat" @click="tabKey = stat.tab">
                        <span class="fh-stat-num">{{ stat.value }}</span>
                        <span class="fh-stat-label">{{ stat.label }}</span>
                    </button>
                </li>
            </ul>
        </section>

        <!-- Listen als Pill-Auswahl -->
        <nav class="fh-tabs" aria-label="Feedback lists">
            <button v-for="tab in visibleTabs" :key="tab.key" type="button" class="fh-tab" :class="{ on: tab.key === tabKey, 'fh-tab--admin': tab.key === 'review' }" :aria-current="tab.key === tabKey ? 'true' : null" @click="tabKey = tab.key">
                {{ tab.label }}
                <span v-if="tab.count !== null && tab.count !== undefined" class="fh-tab-count">{{ tab.count }}</span>
                <span v-if="tab.key === 'review' && pendingCount" class="fh-tab-badge" :aria-label="pendingCount + ' waiting for review'">{{ pendingCount }}</span>
            </button>
        </nav>

        <div class="fh-grid" :class="{ 'fh-grid--single': tabKey === 'review' }">
            <div class="fh-main">
                <template v-if="tabKey !== 'review'">
                    <div class="fh-filters">
                        <v-text-field v-model="search" class="fh-search" placeholder="Search ideas" aria-label="Search ideas" prepend-inner-icon="mdi-magnify" outlined dense rounded hide-details clearable @input="debouncedLoad" />
                        <v-select v-model="sort" class="fh-sort" :items="sortItems" aria-label="Sort" outlined dense rounded hide-details />
                    </div>
                    <div class="fh-chiprow">
                        <span class="fh-chiplabel" id="fh-type-label">Type</span>
                        <v-chip-group v-model="typeFilter" active-class="primary white--text" column aria-labelledby="fh-type-label">
                            <v-chip v-for="type in typeItems" :key="type.value" :value="type.value" small>{{ type.text }}</v-chip>
                        </v-chip-group>
                    </div>
                    <div class="fh-chiprow">
                        <span class="fh-chiplabel" id="fh-suite-label">Suite</span>
                        <v-chip-group v-model="suiteFilter" active-class="primary white--text" column aria-labelledby="fh-suite-label">
                            <v-chip v-for="suite in suiteItems" :key="suite.value" :value="suite.value" small>{{ suite.text }}</v-chip>
                        </v-chip-group>
                    </div>

                    <v-alert v-if="error" type="error" dense outlined class="mt-4">{{ error }}</v-alert>
                    <v-skeleton-loader v-if="loading && !items.length" type="list-item-three-line, list-item-three-line" class="mt-4" />

                    <div class="mt-4">
                        <FeedbackItemCard v-for="item in items" :key="item.id" class="mb-3" :item="item" :admin="isAdmin" :voting="votingId === item.id" :show-status="tabKey !== 'open'" @vote="vote" @show-votes="openVotes" />
                    </div>

                    <div v-if="!loading && !error && !items.length" class="fh-empty">
                        <div class="fh-empty-icon"><v-icon size="30" color="primary" aria-hidden="true">mdi-lightbulb-on-outline</v-icon></div>
                        <p class="fh-empty-text">{{ emptyText }}</p>
                        <v-btn v-if="currentTab.cta" rounded color="primary" @click="openWidget"><v-icon left>mdi-message-plus-outline</v-icon>Give feedback</v-btn>
                    </div>
                </template>

                <FeedbackAdminPanel v-else ref="adminPanel" @pending-count="pendingCount = $event" @changed="onAdminChanged" @show-votes="openVotes" />
            </div>

            <aside v-if="tabKey !== 'review'" class="fh-aside" aria-label="About the Feedback Hub">
                <v-card outlined class="fh-card">
                    <h2 class="fh-card-title">How it works</h2>
                    <ol class="fh-steps">
                        <li><span class="fh-step-num">1</span><span><strong>Share an idea.</strong> Tell us what is not working or what you are missing.</span></li>
                        <li><span class="fh-step-num">2</span><span><strong>We review it.</strong> Approved feedback appears here, without your name.</span></li>
                        <li><span class="fh-step-num">3</span><span><strong>Vote and follow.</strong> The most wanted ideas move up. You see what is planned and done.</span></li>
                    </ol>
                </v-card>

                <v-card v-if="tabKey !== 'mine'" outlined class="fh-card">
                    <h2 class="fh-card-title">Your feedback</h2>
                    <ul v-if="myPreview.length" class="fh-mine">
                        <li v-for="item in myPreview" :key="item.id">
                            <v-icon small :color="statusMeta(item.status).color" aria-hidden="true">{{ statusMeta(item.status).icon }}</v-icon>
                            <span class="fh-mine-title">{{ item.title }}</span>
                            <span class="fh-mine-status">{{ statusMeta(item.status).label }}</span>
                        </li>
                    </ul>
                    <p v-else class="fh-card-text">You have not shared anything yet.</p>
                    <v-btn v-if="myPreview.length" small text rounded color="primary" class="mt-2" @click="tabKey = 'mine'">See all</v-btn>
                    <v-btn v-else small rounded color="primary" class="mt-2" @click="openWidget">Give feedback</v-btn>
                </v-card>
            </aside>
        </div>

        <FeedbackVotesByCompanyDialog :show="votesDialog" :item="votesItem" @close="votesDialog = false" />
    </div>
</template>

<script>
    import FeedbackItemCard from "../components/feedback/FeedbackItemCard.vue"
    import FeedbackAdminPanel from "../components/feedback/FeedbackAdminPanel.vue"
    import FeedbackVotesByCompanyDialog from "../components/feedback/FeedbackVotesByCompanyDialog.vue"
    import { TYPE_META, STATUS_META, SUITE_LABELS } from "../components/feedback/feedbackMeta"

    export default {
        name: "FeedbackHub",
        components: { FeedbackItemCard, FeedbackAdminPanel, FeedbackVotesByCompanyDialog },
        data() {
            return {
                // Links aus Benachrichtigung und Widget oeffnen direkt "My feedback"
                tabKey: this.$route.query.tab === "mine" ? "mine" : "open",
                items: [],
                myItems: null,
                counts: null,
                loading: false,
                error: "",
                search: "",
                typeFilter: null,
                suiteFilter: null,
                sort: "top",
                votingId: null,
                pendingCount: 0,
                votesDialog: false,
                votesItem: null,
                searchTimer: null,
                requestId: 0,
                sortItems: [
                    { value: "top", text: "Most votes" },
                    { value: "new", text: "Newest" }
                ],
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH)
            }
        },
        computed: {
            isAdmin() {
                return !!this.$store.state.admin
            },
            visibleTabs() {
                const c = this.counts
                const tabs = [
                    { key: "open", label: "Open for voting", statuses: ["open"], count: c ? c.open : null, cta: true, empty: "Nothing to vote on right now. Be the first to share an idea." },
                    { key: "planned", label: "Planned & in progress", statuses: ["planned", "in_progress"], count: c ? c.planned + c.in_progress : null, empty: "Nothing is planned yet. Vote for ideas to help us decide." },
                    { key: "done", label: "Done", statuses: ["done"], count: c ? c.done : null, empty: "Nothing has been completed yet." },
                    { key: "rejected", label: "Rejected", statuses: ["rejected"], count: c ? c.rejected : null, empty: "No feedback has been rejected." },
                    { key: "mine", label: "My feedback", statuses: null, count: this.myItems ? this.myItems.length : null, cta: true, empty: "You have not shared any feedback yet." }
                ]
                if (this.isAdmin) tabs.push({ key: "review", label: "Review", count: null })
                return tabs
            },
            currentTab() {
                return this.visibleTabs.find((t) => t.key === this.tabKey) || this.visibleTabs[0]
            },
            emptyText() {
                const filtered = this.search || this.typeFilter || this.suiteFilter
                return filtered ? "No ideas match your filters." : this.currentTab.empty || ""
            },
            heroStats() {
                const c = this.counts || { open: 0, planned: 0, in_progress: 0, done: 0 }
                return [
                    { tab: "open", label: "Open for voting", value: c.open },
                    { tab: "planned", label: "Planned & in progress", value: c.planned + c.in_progress },
                    { tab: "done", label: "Done", value: c.done }
                ]
            },
            myPreview() {
                return (this.myItems || []).slice(0, 3)
            },
            typeItems() {
                return Object.keys(TYPE_META).map((value) => ({ value, text: TYPE_META[value].label }))
            },
            suiteItems() {
                return Object.keys(SUITE_LABELS).map((value) => ({ value, text: SUITE_LABELS[value] }))
            }
        },
        watch: {
            tabKey() {
                this.items = []
                this.load()
            },
            typeFilter() {
                this.load()
            },
            suiteFilter() {
                this.load()
            },
            sort() {
                this.load()
            },
            "$route.query.tab"(val) {
                if (val === "mine") this.showMine()
            }
        },
        methods: {
            statusMeta(status) {
                return STATUS_META[status] || STATUS_META.open
            },
            body(extra) {
                return Object.assign({ user: localStorage.getItem("user"), company_id: localStorage.getItem("company_id") }, extra || {})
            },
            post(path, extra) {
                return this.$http.post(process.env.VUE_APP_APIURL + path, this.body(extra), { headers: { Authorization: this.auth } })
            },
            openWidget() {
                this.$root.$emit("openFeedbackWidget")
            },
            showMine() {
                this.tabKey = "mine"
            },
            debouncedLoad() {
                clearTimeout(this.searchTimer)
                this.searchTimer = setTimeout(this.load, 300)
            },
            load() {
                const tab = this.currentTab
                if (tab.key === "review") return
                const requestId = ++this.requestId
                this.loading = true
                this.error = ""
                const request =
                    tab.key === "mine"
                        ? this.post("/feedback/items/mine")
                        : this.post("/feedback/items/list", { statuses: tab.statuses, type: this.typeFilter, suite: this.suiteFilter, q: this.search || "", sort: this.sort })
                request
                    .then((response) => {
                        if (requestId !== this.requestId) return
                        this.items = response.body.items || []
                        if (tab.key === "mine") this.myItems = this.items
                    })
                    .catch(() => {
                        if (requestId !== this.requestId) return
                        this.error = "The feedback could not be loaded. Please try again."
                    })
                    .finally(() => {
                        if (requestId === this.requestId) this.loading = false
                    })
            },
            loadCounts() {
                this.post("/feedback/items/stats")
                    .then((response) => {
                        this.counts = response.body.counts || null
                    })
                    .catch(() => {})
            },
            loadMine() {
                this.post("/feedback/items/mine")
                    .then((response) => {
                        this.myItems = response.body.items || []
                    })
                    .catch(() => {})
            },
            vote(item) {
                if (this.votingId !== null) return
                this.votingId = item.id
                this.post("/feedback/items/" + item.id + "/vote")
                    .then((response) => {
                        item.voted = !!response.body.voted
                        item.votes = response.body.votes
                    })
                    .catch((error) => {
                        this.error = (error && error.body && error.body.error) || "Your vote could not be saved."
                    })
                    .finally(() => {
                        this.votingId = null
                    })
            },
            openVotes(item) {
                this.votesItem = item
                this.votesDialog = true
            },
            onAdminChanged() {
                // Oeffentliche Listen und Zahlen sind nach einer Admin-Aktion nicht mehr aktuell
                this.items = []
                this.loadCounts()
            },
            loadPendingCount() {
                if (!this.isAdmin) return
                this.post("/feedback/admin/items")
                    .then((response) => {
                        this.pendingCount = response.body.pending || 0
                    })
                    .catch(() => {})
            },
            onSubmitted() {
                if (this.tabKey === "mine") this.load()
                else this.loadMine()
                this.loadPendingCount()
            }
        },
        mounted() {
            this.$root.$on("feedbackSubmitted", this.onSubmitted)
            this.$root.$on("feedbackShowMine", this.showMine)
            this.load()
            this.loadCounts()
            if (this.tabKey !== "mine") this.loadMine()
            this.loadPendingCount()
        },
        beforeDestroy() {
            clearTimeout(this.searchTimer)
            this.$root.$off("feedbackSubmitted", this.onSubmitted)
            this.$root.$off("feedbackShowMine", this.showMine)
        }
    }
</script>

<style scoped>
    .fh {
        max-width: 1280px;
        margin: 0 auto;
        padding: 24px 16px 64px;
    }

    /* Hero */
    .fh-hero {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 32px;
        padding: 32px 36px;
        margin-bottom: 24px;
        border-radius: 20px;
        overflow: hidden;
        color: white;
        background: linear-gradient(140deg, var(--v-hero_gradient1-base, var(--v-primary-base)), var(--v-hero_gradient2-base, var(--v-primary-base)));
        box-shadow: 0 12px 36px rgba(0, 0, 0, 0.16);
    }
    .fh-hero::before {
        content: "";
        position: absolute;
        width: 440px;
        height: 440px;
        right: -150px;
        top: -200px;
        border-radius: 50%;
        background: radial-gradient(circle, color-mix(in srgb, var(--v-hero_glow-base, var(--v-lightgreen-base)) 30%, transparent), transparent 62%);
        pointer-events: none;
    }
    .fh-hero-text,
    .fh-stats {
        position: relative;
    }
    .fh-hero-text {
        max-width: 560px;
    }
    .fh-eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--v-lightgreen-base);
    }
    .fh-eyebrow .v-icon {
        color: var(--v-lightgreen-base);
    }
    .fh-hero-title {
        font-family: "CalSans", sans-serif;
        font-weight: normal;
        font-size: 34px;
        line-height: 1.15;
        letter-spacing: -0.02em;
        margin-bottom: 8px;
    }
    .fh-hero-sub {
        margin: 0 0 20px;
        font-size: 15px;
        line-height: 1.5;
        opacity: 0.85;
    }
    .fh-cta {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        border: 0;
        border-radius: 999px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: var(--v-hero_gradient2-base, black);
        background: var(--v-lightgreen-base);
        transition: filter 0.15s ease, transform 0.15s ease;
    }
    .fh-cta .v-icon {
        color: inherit;
    }
    .fh-cta:hover {
        filter: brightness(1.08);
        transform: translateY(-1px);
    }
    .fh-cta:focus-visible,
    .fh-stat:focus-visible,
    .fh-tab:focus-visible {
        outline: 2px solid var(--v-lightgreen-base);
        outline-offset: 2px;
    }
    .fh-stats {
        display: grid;
        grid-template-columns: repeat(3, minmax(110px, 1fr));
        gap: 12px;
        margin: 0;
        padding: 0;
        list-style: none;
    }
    .fh-stat {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        padding: 14px 20px;
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 16px;
        background: rgba(255, 255, 255, 0.07);
        color: inherit;
        text-align: left;
        cursor: pointer;
        transition: border-color 0.15s ease, background 0.15s ease;
    }
    .fh-stat:hover {
        border-color: var(--v-lightgreen-base);
        background: rgba(255, 255, 255, 0.12);
    }
    .fh-stat-num {
        font-family: "CalSans", sans-serif;
        font-size: 32px;
        line-height: 1.1;
    }
    .fh-stat-label {
        margin-top: 4px;
        font-size: 12px;
        opacity: 0.8;
    }

    /* Listen-Auswahl */
    .fh-tabs {
        display: flex;
        gap: 8px;
        margin-bottom: 20px;
        padding-bottom: 4px;
        overflow-x: auto;
    }
    .fh-tab {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        border: 1px solid rgba(128, 128, 128, 0.35);
        border-radius: 999px;
        background: transparent;
        color: inherit;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        cursor: pointer;
        transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .fh-tab:hover {
        border-color: var(--v-primary-base);
    }
    .fh-tab.on {
        background: var(--v-primary-base);
        border-color: var(--v-primary-base);
        color: white;
    }
    .fh-tab--admin {
        margin-left: auto;
        border-style: dashed;
    }
    .fh-tab-count {
        font-size: 12px;
        font-weight: 700;
        opacity: 0.7;
    }
    .fh-tab.on .fh-tab-count {
        opacity: 1;
    }
    .fh-tab-badge {
        min-width: 20px;
        padding: 0 6px;
        border-radius: 999px;
        font-size: 11px;
        line-height: 20px;
        text-align: center;
        color: white;
        background: var(--v-error-base);
    }

    /* Inhalt */
    .fh-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 320px;
        gap: 24px;
        align-items: start;
    }
    .fh-grid--single {
        grid-template-columns: minmax(0, 1fr);
    }
    .fh-filters {
        display: flex;
        gap: 12px;
        margin-bottom: 12px;
    }
    .fh-search {
        flex: 1;
    }
    .fh-sort {
        flex: 0 0 180px;
    }
    .fh-chiprow {
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }
    .fh-chiplabel {
        flex: 0 0 44px;
        padding-top: 8px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        opacity: 0.6;
    }
    .fh-empty {
        padding: 48px 16px;
        text-align: center;
    }
    .fh-empty-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        position: relative;
        width: 64px;
        height: 64px;
        margin-bottom: 12px;
        border-radius: 50%;
        overflow: hidden;
    }
    .fh-empty-icon::before {
        content: "";
        position: absolute;
        inset: 0;
        background: var(--v-primary-base);
        opacity: 0.12;
    }
    .fh-empty-text {
        margin-bottom: 16px;
        opacity: 0.75;
    }

    /* Seitenleiste */
    .fh-aside {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }
    .fh-card.v-card {
        padding: 20px;
        border-radius: 16px;
        border-color: rgba(128, 128, 128, 0.25);
    }
    .fh-card-title {
        margin-bottom: 12px;
        font-family: "CalSans", sans-serif;
        font-weight: normal;
        font-size: 18px;
    }
    .fh-card-text {
        margin: 0;
        font-size: 14px;
        opacity: 0.75;
    }
    .fh-steps {
        margin: 0;
        padding: 0;
        list-style: none;
    }
    .fh-steps li {
        display: flex;
        gap: 12px;
        margin-bottom: 14px;
        font-size: 13.5px;
        line-height: 1.45;
    }
    .fh-steps li:last-child {
        margin-bottom: 0;
    }
    .fh-step-num {
        flex: 0 0 26px;
        height: 26px;
        border-radius: 50%;
        font-size: 13px;
        font-weight: 700;
        line-height: 26px;
        text-align: center;
        color: white;
        background: var(--v-primary-base);
    }
    .fh-mine {
        margin: 0;
        padding: 0;
        list-style: none;
    }
    .fh-mine li {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 0;
        font-size: 13.5px;
    }
    .fh-mine-title {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .fh-mine-status {
        font-size: 12px;
        opacity: 0.65;
        white-space: nowrap;
    }

    @media (max-width: 959px) {
        .fh-hero {
            flex-direction: column;
            align-items: stretch;
            padding: 24px 20px;
        }
        .fh-hero-title {
            font-size: 28px;
        }
        .fh-grid {
            grid-template-columns: minmax(0, 1fr);
        }
    }
    @media (max-width: 599px) {
        .fh-filters {
            flex-direction: column;
        }
        .fh-sort {
            flex: 1 1 auto;
        }
        .fh-stat {
            padding: 12px;
        }
        .fh-stat-num {
            font-size: 26px;
        }
        .fh-chiprow {
            flex-direction: column;
            gap: 0;
        }
        .fh-chiplabel {
            padding-top: 4px;
        }
    }
</style>
